import { supabase } from '../supabaseClient';
import { sendNotificationToUser } from './supabaseHelpers';

// ============================================
// CHANNEL OPERATIONS
// ============================================

/**
 * Fetch all channels the current user has access to
 * Shows all public channels + private channels where user is a member
 */
export const fetchUserChannels = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  // Get all channel IDs where user is a member (for private channel access)
  const { data: memberRecords } = await supabase
    .from('team_chat')
    .select('channel_id')
    .eq('record_type', 'member')
    .eq('user_id', user.id);

  const memberChannelIds = memberRecords ? memberRecords.map(m => m.channel_id) : [];

  // Fetch all channels (public + private channels where user is member)
  const { data: channelRecords, error } = await supabase
    .from('team_chat')
    .select('*')
    .eq('record_type', 'channel')
    .order('created_at', { ascending: false });

  if (error) return { data: null, error };

  // Filter channels: show all public channels + private channels where user is a member
  const filteredChannels = channelRecords.filter(record => {
    // Show if channel is public (not private)
    if (!record.is_private) return true;
    // Show if channel is private and user is a member
    return memberChannelIds.includes(record.id);
  });

  // Transform to match expected format
  const channels = filteredChannels.map(record => ({
    id: record.id,
    name: record.channel_name,
    description: record.channel_description,
    is_private: record.is_private,
    created_by: record.created_by,
    created_at: record.created_at,
    updated_at: record.updated_at
  }));

  return { data: channels, error: null };
};

/**
 * Create a new channel
 */
export const createChannel = async (channelData) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  // Create channel record
  const { data: channelRecord, error: channelError } = await supabase
    .from('team_chat')
    .insert({
      record_type: 'channel',
      channel_name: channelData.name,
      channel_description: channelData.description,
      is_private: channelData.is_private || false,
      created_by: user.id
    })
    .select()
    .single();

  if (channelError) return { data: null, error: channelError };

  // Add creator as admin member
  const { error: memberError } = await supabase
    .from('team_chat')
    .insert({
      record_type: 'member',
      channel_id: channelRecord.id,
      user_id: user.id,
      user_role: 'admin',
      created_by: user.id
    });

  if (memberError) return { data: null, error: memberError };

  // Transform to match expected format
  const channel = {
    id: channelRecord.id,
    name: channelRecord.channel_name,
    description: channelRecord.channel_description,
    is_private: channelRecord.is_private,
    created_by: channelRecord.created_by,
    created_at: channelRecord.created_at,
    updated_at: channelRecord.updated_at
  };

  return { data: channel, error: null };
};

/**
 * Update channel details
 */
export const updateChannel = async (channelId, updates) => {
  const updateData = {};
  if (updates.name) updateData.channel_name = updates.name;
  if (updates.description !== undefined) updateData.channel_description = updates.description;
  if (updates.is_private !== undefined) updateData.is_private = updates.is_private;

  const { data, error } = await supabase
    .from('team_chat')
    .update(updateData)
    .eq('id', channelId)
    .eq('record_type', 'channel')
    .select()
    .single();

  if (error) return { data: null, error };

  // Transform to match expected format
  const channel = {
    id: data.id,
    name: data.channel_name,
    description: data.channel_description,
    is_private: data.is_private,
    created_by: data.created_by,
    created_at: data.created_at,
    updated_at: data.updated_at
  };

  return { data: channel, error: null };
};

/**
 * Delete a channel
 */
export const deleteChannel = async (channelId) => {
  // Delete all records related to this channel (members, messages, etc.)
  const { error } = await supabase
    .from('team_chat')
    .delete()
    .or(`id.eq.${channelId},channel_id.eq.${channelId}`);

  return { error };
};

// ============================================
// CHANNEL MEMBER OPERATIONS
// ============================================

/**
 * Fetch members of a specific channel
 */
export const fetchChannelMembers = async (channelId) => {
  const { data: memberRecords, error } = await supabase
    .from('team_chat')
    .select('*')
    .eq('record_type', 'member')
    .eq('channel_id', channelId)
    .order('created_at', { ascending: true });

  if (error) return { data: null, error };

  // Fetch user details for all members
  const userIds = memberRecords.map(m => m.user_id);
  const { data: users } = await supabase
    .from('users')
    .select('id, name, email, is_active, inactive_user_name')
    .in('id', userIds);

  // Transform to match expected format
  const members = memberRecords.map(record => ({
    id: record.id,
    channel_id: record.channel_id,
    user_id: record.user_id,
    role: record.user_role,
    joined_at: record.created_at,
    user: users?.find(u => u.id === record.user_id) || null
  }));

  return { data: members, error: null };
};

/**
 * Add member to channel
 */
export const addChannelMember = async (channelId, userId, role = 'member') => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  const { data: channelData } = await supabase
    .from('team_chat')
    .select('channel_name')
    .eq('id', channelId)
    .eq('record_type', 'channel')
    .single();

  // Get admin and new member details
  const { data: adminData } = await supabase
    .from('users')
    .select('name, email')
    .eq('id', user.id)
    .single();

  const { data: newMemberData } = await supabase
    .from('users')
    .select('name, email')
    .eq('id', userId)
    .single();

  const { data, error } = await supabase
    .from('team_chat')
    .insert({
      record_type: 'member',
      channel_id: channelId,
      user_id: userId,
      user_role: role,
      created_by: user.id
    })
    .select()
    .single();

  if (error) return { data: null, error };

  // Create system message for member addition
  const adminName = adminData?.name || adminData?.email?.split('@')[0] || 'Admin';
  const memberName = newMemberData?.name || newMemberData?.email?.split('@')[0] || 'User';

  await supabase
    .from('team_chat')
    .insert({
      record_type: 'message',
      channel_id: channelId,
      user_id: user.id,
      message_text: `${adminName} invited ${memberName} to the channel`,
      is_system_message: true,
      created_by: user.id
    });

  // Transform to match expected format
  const member = {
    id: data.id,
    channel_id: data.channel_id,
    user_id: data.user_id,
    role: data.user_role,
    joined_at: data.created_at
  };

  const channelName = channelData?.channel_name || 'a channel';
  const inviterName = adminData?.name || adminData?.email?.split('@')[0] || 'Someone';

  try {
    await sendNotificationToUser({
      userId,
      type: 'team_chat',
      referenceId: channelId,
      title: `${inviterName} invited you to ${channelName}`,
      message: 'You have been added to a new channel.',
      link: '/team-chat'
    });
  } catch (notificationError) {
    console.error('Notification fallback failed for added channel member:', notificationError);
  }

  return { data: member, error: null };
};

/**
 * Remove member from channel
 * Prevents removal of channel creator
 * Prevents non-creators from removing admins
 */
export const removeChannelMember = async (channelId, userId) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('Not authenticated') };

  // Fetch channel data to check creator
  const { data: channelData, error: channelError } = await supabase
    .from('team_chat')
    .select('created_by')
    .eq('id', channelId)
    .eq('record_type', 'channel')
    .single();

  if (channelError || !channelData) {
    return { error: channelError || new Error('Channel not found') };
  }

  // Prevent removal of channel creator
  if (channelData.created_by === userId) {
    return { error: new Error('Cannot remove the channel creator') };
  }

  // Check if the user being removed is an admin
  const { data: memberData } = await supabase
    .from('team_chat')
    .select('user_role')
    .eq('record_type', 'member')
    .eq('channel_id', channelId)
    .eq('user_id', userId)
    .single();

  // If removing an admin, only the creator can do it
  if (memberData && memberData.user_role === 'admin' && channelData.created_by !== user.id) {
    return { error: new Error('Only the channel creator can remove admins') };
  }

  // Get admin and removed member details for system message
  const { data: adminData } = await supabase
    .from('users')
    .select('name, email')
    .eq('id', user.id)
    .single();

  const { data: removedMemberData } = await supabase
    .from('users')
    .select('name, email')
    .eq('id', userId)
    .single();

  const { error } = await supabase
    .from('team_chat')
    .delete()
    .eq('record_type', 'member')
    .eq('channel_id', channelId)
    .eq('user_id', userId);

  if (error) return { error };

  // Create system message for member removal
  const adminName = adminData?.name || adminData?.email?.split('@')[0] || 'Admin';
  const memberName = removedMemberData?.name || removedMemberData?.email?.split('@')[0] || 'User';

  await supabase
    .from('team_chat')
    .insert({
      record_type: 'message',
      channel_id: channelId,
      user_id: user.id,
      message_text: `${adminName} removed ${memberName} from the channel`,
      is_system_message: true,
      created_by: user.id
    });

  const channelName = channelData?.channel_name || 'a channel';

  try {
    await sendNotificationToUser({
      userId,
      type: 'team_chat',
      referenceId: channelId,
      title: `You were removed from ${channelName}`,
      message: 'Access revoked.',
      link: '/team-chat'
    });
  } catch (notificationError) {
    console.error('Notification fallback failed for removed channel member:', notificationError);
  }

  return { error: null };
};

/**
 * Update member role
 * Only the channel creator can promote/demote admins
 */
export const updateMemberRole = async (channelId, userId, role) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: new Error('Not authenticated') };

  // Fetch channel data to verify creator
  const { data: channelData, error: channelError } = await supabase
    .from('team_chat')
    .select('created_by')
    .eq('id', channelId)
    .eq('record_type', 'channel')
    .single();

  if (channelError || !channelData) {
    return { data: null, error: channelError || new Error('Channel not found') };
  }

  // Only the creator can change member roles
  if (channelData.created_by !== user.id) {
    return { data: null, error: new Error('Only the channel creator can manage admin roles') };
  }

  const { data, error } = await supabase
    .from('team_chat')
    .update({ user_role: role })
    .eq('record_type', 'member')
    .eq('channel_id', channelId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) return { data: null, error };

  // Transform to match expected format
  const member = {
    id: data.id,
    channel_id: data.channel_id,
    user_id: data.user_id,
    role: data.user_role,
    joined_at: data.created_at
  };

  return { data: member, error: null };
};

// ============================================
// MESSAGE OPERATIONS
// ============================================

/**
 * Fetch messages for a channel with pagination
 * Also fetches polls and their votes
 */
export const fetchChannelMessages = async (channelId, limit = 50, before = null) => {
  // Fetch messages
  let messageQuery = supabase
    .from('team_chat')
    .select('*')
    .eq('record_type', 'message')
    .eq('channel_id', channelId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (before) {
    messageQuery = messageQuery.lt('created_at', before);
  }

  // Fetch polls definition
  let pollQuery = supabase
    .from('team_chat')
    .select('*')
    .eq('record_type', 'poll')
    .eq('channel_id', channelId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (before) {
    pollQuery = pollQuery.lt('created_at', before);
  }

  // Fetch messages and polls in parallel
  const [messagesResult, pollsResult] = await Promise.all([
    messageQuery,
    pollQuery
  ]);

  const { data: allMessageRecords, error: messageError } = { data: messagesResult.data, error: messagesResult.error };
  const { data: pollRecords } = { data: pollsResult.data };

  if (messageError) return { data: null, error: messageError };

  // Separate regular messages from list messages
  const messageRecords = [];
  const listRecords = [];

  allMessageRecords?.forEach(record => {
    try {
      const parsed = JSON.parse(record.message_text);
      if (parsed.__type === 'list') {
        listRecords.push(record);
      } else {
        messageRecords.push(record);
      }
    } catch (e) {
      // Not JSON or invalid JSON, treat as regular message
      messageRecords.push(record);
    }
  });

  // Combine messages, polls, and lists
  const allRecords = [...messageRecords, ...(pollRecords || []), ...listRecords];
  allRecords.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Determine needed auxiliary data
  const userIds = [...new Set(allRecords.map(r => r.user_id || r.created_by).filter(Boolean))];
  const pollIds = pollRecords ? pollRecords.map(p => p.id) : [];
  const parentMessageIds = messageRecords
    .filter(m => m.parent_message_id)
    .map(m => m.parent_message_id);

  // Fetch auxiliary data in parallel
  const [usersResult, votesResult, parentMessagesResult] = await Promise.all([
    // 1. Users
    userIds.length > 0 ? supabase.from('users').select('id, name, email, is_active, inactive_user_name').in('id', userIds) : Promise.resolve({ data: [] }),
    // 2. Votes (only if polls exist)
    pollIds.length > 0 ? supabase.from('team_chat').select('*').eq('record_type', 'vote').in('parent_message_id', pollIds) : Promise.resolve({ data: [] }),
    // 3. Parent Messages (only if replies exist)
    parentMessageIds.length > 0 ? supabase.from('team_chat').select('*').eq('record_type', 'message').in('id', parentMessageIds) : Promise.resolve({ data: [] })
  ]);

  const users = usersResult.data || [];
  const votes = votesResult.data || [];
  const parentRecords = parentMessagesResult.data || [];

  // Process Votes
  let votesByPoll = {};
  votes.forEach(vote => {
    if (!votesByPoll[vote.parent_message_id]) {
      votesByPoll[vote.parent_message_id] = [];
    }
    votesByPoll[vote.parent_message_id].push(vote);
  });

  // Process Parent Messages
  let parentMessages = {};
  parentRecords.forEach(record => {
    parentMessages[record.id] = {
      id: record.id,
      message: record.message_text,
      user: users.find(u => u.id === record.user_id) || null
    };
  });

  // Transform to match expected format and reverse for chronological order
  const items = allRecords.map(record => {
    if (record.record_type === 'poll') {
      return {
        id: record.id,
        type: 'poll',
        channel_id: record.channel_id,
        user_id: record.created_by,
        question: record.poll_question,
        options: record.poll_options,
        ends_at: record.poll_ends_at,
        votes: votesByPoll[record.id] || [],
        created_at: record.created_at,
        user: users?.find(u => u.id === record.created_by) || null
      };
    } else if (record.record_type === 'message') {
      // Check if this is a list message
      try {
        const parsed = JSON.parse(record.message_text);
        if (parsed.__type === 'list') {
          const listTitle = parsed.list_title || '';
          const listItems = parsed.list_items || [];

          return {
            id: record.id,
            type: 'list',
            channel_id: record.channel_id,
            user_id: record.created_by,
            list_title: listTitle,
            list_items: listItems,
            message_text: record.message_text,
            created_at: record.created_at,
            updated_at: record.updated_at,
            user: users?.find(u => u.id === record.created_by) || null
          };
        }
      } catch (e) {
        // Not a list, fall through to regular message
      }

      // Regular message
      const message = {
        id: record.id,
        type: 'message',
        channel_id: record.channel_id,
        user_id: record.user_id || record.created_by,
        message: record.message_text,
        mentions: record.mentions,
        is_edited: record.is_edited || false,
        is_system_message: record.is_system_message || false,
        parent_message_id: record.parent_message_id,
        created_at: record.created_at,
        updated_at: record.updated_at,
        user: users?.find(u => u.id === (record.user_id || record.created_by)) || null
      };

      // Add parent message data if this is a reply
      if (record.parent_message_id && parentMessages[record.parent_message_id]) {
        message.parent_message = parentMessages[record.parent_message_id];
      }

      return message;
    } else {

      const message = {
        id: record.id,
        type: 'message',
        channel_id: record.channel_id,
        user_id: record.user_id,
        message: record.message_text,
        mentions: record.mentions,
        is_edited: record.is_edited,
        is_system_message: record.is_system_message || false,
        parent_message_id: record.parent_message_id,
        created_at: record.created_at,
        updated_at: record.updated_at,
        user: users?.find(u => u.id === record.user_id) || null
      };

      // Add parent message data if this is a reply
      if (record.parent_message_id && parentMessages[record.parent_message_id]) {
        message.parent_message = parentMessages[record.parent_message_id];
      }

      return message;
    }
  }).reverse();

  return { data: items, error: null };
};

/**
 * Send a message to a channel
 * @param {string} channelId - Channel ID
 * @param {string} message - Message text
 * @param {Array} mentions - Array of user IDs mentioned
 * @param {string|null} parentMessageId - ID of parent message if this is a reply
 */
export const sendMessage = async (channelId, message, mentions = [], parentMessageId = null) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  const insertData = {
    record_type: 'message',
    channel_id: channelId,
    user_id: user.id,
    message_text: message,
    mentions: mentions.length > 0 ? mentions : null,
    created_by: user.id
  };

  // Add parent_message_id if this is a reply
  if (parentMessageId) {
    insertData.parent_message_id = parentMessageId;
  }

  const { data: messageRecord, error } = await supabase
    .from('team_chat')
    .insert(insertData)
    .select()
    .single();

  if (error) return { data: null, error };

  // Fetch user details
  const { data: userData } = await supabase
    .from('users')
    .select('id, name, email, is_active, inactive_user_name')
    .eq('id', user.id)
    .single();

  // Fetch parent message data if this is a reply
  let parentMessageData = null;
  if (parentMessageId) {
    const { data: parentRecord } = await supabase
      .from('team_chat')
      .select('*')
      .eq('id', parentMessageId)
      .eq('record_type', 'message')
      .single();

    if (parentRecord) {
      // Fetch parent message user
      const { data: parentUser } = await supabase
        .from('users')
        .select('id, name, email, is_active, inactive_user_name')
        .eq('id', parentRecord.user_id)
        .single();

      parentMessageData = {
        id: parentRecord.id,
        message: parentRecord.message_text,
        user: parentUser
      };
    }
  }

  // Transform to match expected format
  const formattedMessage = {
    id: messageRecord.id,
    channel_id: messageRecord.channel_id,
    user_id: messageRecord.user_id,
    message: messageRecord.message_text,
    mentions: messageRecord.mentions,
    is_edited: messageRecord.is_edited,
    parent_message_id: messageRecord.parent_message_id,
    created_at: messageRecord.created_at,
    updated_at: messageRecord.updated_at,
    user: userData
  };

  // Add parent message data if available
  if (parentMessageData) {
    formattedMessage.parent_message = parentMessageData;
  }

  return { data: formattedMessage, error: null };
};

/**
 * Update a message
 */
export const updateMessage = async (messageId, newMessage) => {
  const { data, error } = await supabase
    .from('team_chat')
    .update({
      message_text: newMessage,
      is_edited: true
    })
    .eq('id', messageId)
    .eq('record_type', 'message')
    .select()
    .single();

  if (error) return { data: null, error };

  // Transform to match expected format
  const message = {
    id: data.id,
    channel_id: data.channel_id,
    user_id: data.user_id,
    message: data.message_text,
    mentions: data.mentions,
    is_edited: data.is_edited,
    created_at: data.created_at,
    updated_at: data.updated_at
  };

  return { data: message, error: null };
};

/**
 * Delete a message
 */
export const deleteMessage = async (messageId) => {
  const { error } = await supabase
    .from('team_chat')
    .delete()
    .eq('id', messageId)
    .eq('record_type', 'message');

  return { error };
};

// ============================================
// REACTION OPERATIONS
// ============================================

/**
 * Add reaction to a message
 */
export const addReaction = async (messageId, reaction) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  // Get the channel_id from the message
  const { data: message } = await supabase
    .from('team_chat')
    .select('channel_id')
    .eq('id', messageId)
    .eq('record_type', 'message')
    .single();

  if (!message) return { data: null, error: 'Message not found' };

  const { data, error } = await supabase
    .from('team_chat')
    .insert({
      record_type: 'reaction',
      channel_id: message.channel_id,
      parent_message_id: messageId,
      user_id: user.id,
      reaction_type: reaction,
      created_by: user.id
    })
    .select()
    .single();

  return { data, error };
};

/**
 * Remove reaction from a message
 */
export const removeReaction = async (messageId, reaction) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: 'Not authenticated' };

  const { error } = await supabase
    .from('team_chat')
    .delete()
    .eq('record_type', 'reaction')
    .eq('parent_message_id', messageId)
    .eq('user_id', user.id)
    .eq('reaction_type', reaction);

  return { error };
};

// ============================================
// POLL OPERATIONS
// ============================================

/**
 * Create a poll in a channel
 */
export const createPoll = async (channelId, question, options, endsAt = null) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  // Format options as JSONB array with vote counts
  const formattedOptions = options.map(option => ({
    text: option,
    votes: 0
  }));

  const { data, error } = await supabase
    .from('team_chat')
    .insert({
      record_type: 'poll',
      channel_id: channelId,
      user_id: user.id,
      poll_question: question,
      poll_options: formattedOptions,
      poll_ends_at: endsAt,
      created_by: user.id
    })
    .select()
    .single();

  return { data, error };
};

/**
 * Fetch polls for a channel
 */
export const fetchChannelPolls = async (channelId) => {
  const { data: pollRecords, error } = await supabase
    .from('team_chat')
    .select('*')
    .eq('record_type', 'poll')
    .eq('channel_id', channelId)
    .order('created_at', { ascending: false });

  if (error) return { data: null, error };

  // Fetch votes for all polls
  const pollIds = pollRecords.map(p => p.id);
  const { data: voteRecords } = await supabase
    .from('team_chat')
    .select('*')
    .eq('record_type', 'vote')
    .in('channel_id', [channelId]);

  // Transform to match expected format
  const polls = pollRecords.map(record => ({
    id: record.id,
    channel_id: record.channel_id,
    created_by: record.created_by,
    question: record.poll_question,
    options: record.poll_options,
    ends_at: record.poll_ends_at,
    created_at: record.created_at,
    poll_votes: voteRecords?.filter(v => v.parent_message_id === record.id) || []
  }));

  return { data: polls, error: null };
};

/**
 * Vote on a poll
 */
export const voteOnPoll = async (pollId, optionIndex) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  // Get poll details
  const { data: poll } = await supabase
    .from('team_chat')
    .select('channel_id')
    .eq('id', pollId)
    .eq('record_type', 'poll')
    .single();

  if (!poll) return { data: null, error: 'Poll not found' };

  // Check if user already voted
  const { data: existingVote } = await supabase
    .from('team_chat')
    .select('*')
    .eq('record_type', 'vote')
    .eq('parent_message_id', pollId)
    .eq('user_id', user.id)
    .single();

  if (existingVote) {
    // Update existing vote
    const { data, error } = await supabase
      .from('team_chat')
      .update({ vote_option_index: optionIndex })
      .eq('id', existingVote.id)
      .select()
      .single();

    return { data, error };
  } else {
    // Insert new vote
    const { data, error } = await supabase
      .from('team_chat')
      .insert({
        record_type: 'vote',
        channel_id: poll.channel_id,
        parent_message_id: pollId,
        user_id: user.id,
        vote_option_index: optionIndex,
        created_by: user.id
      })
      .select()
      .single();

    return { data, error };
  }
};

// ============================================
// REAL-TIME SUBSCRIPTIONS
// ============================================

/**
 * Subscribe to new messages in a channel
 */
export const subscribeToMessages = (channelId, callback) => {
  const channelName = `messages:${channelId}`;
  console.log('[Subscription] Creating subscription:', channelName);

  const subscription = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'team_chat',
        filter: `channel_id=eq.${channelId}`
      },
      async (payload) => {
        console.log('[Subscription] Received INSERT event:', payload);

        // Only process message and poll records
        if (!['message', 'poll'].includes(payload.new.record_type)) return;

        if (payload.new.record_type === 'message') {
          // Fetch user data for the new message
          const { data: user } = await supabase
            .from('users')
            .select('id, name, email, is_active, inactive_user_name')
            .eq('id', payload.new.user_id)
            .single();

          // Check if this is a list message
          let isListMessage = false;
          let parsedList = null;
          try {
            const parsed = JSON.parse(payload.new.message_text);
            if (parsed.__type === 'list') {
              isListMessage = true;
              parsedList = parsed;
            }
          } catch (e) {
            // Not a list message, continue as normal message
          }

          // If it's a list message, format as list
          if (isListMessage && parsedList) {
            const listItem = {
              id: payload.new.id,
              type: 'list',
              channel_id: payload.new.channel_id,
              user_id: payload.new.user_id,
              list_title: parsedList.list_title || '',
              list_items: parsedList.list_items || [],
              message_text: payload.new.message_text,
              created_at: payload.new.created_at,
              updated_at: payload.new.updated_at,
              user: user
            };

            console.log('[Subscription] Transformed list:', listItem);
            callback(listItem);
            return;
          }

          // Fetch parent message data if this is a reply
          let parentMessageData = null;
          if (payload.new.parent_message_id) {
            const { data: parentRecord } = await supabase
              .from('team_chat')
              .select('*')
              .eq('id', payload.new.parent_message_id)
              .eq('record_type', 'message')
              .single();

            if (parentRecord) {
              // Fetch parent message user
              const { data: parentUser } = await supabase
                .from('users')
                .select('id, name, email, is_active, inactive_user_name')
                .eq('id', parentRecord.user_id)
                .single();

              parentMessageData = {
                id: parentRecord.id,
                message: parentRecord.message_text,
                user: parentUser
              };
            }
          }

          // Transform to match expected format (regular message)
          const message = {
            id: payload.new.id,
            channel_id: payload.new.channel_id,
            user_id: payload.new.user_id,
            message: payload.new.message_text,
            parent_message_id: payload.new.parent_message_id,
            mentions: payload.new.mentions,
            is_edited: payload.new.is_edited,
            is_system_message: payload.new.is_system_message || false,
            created_at: payload.new.created_at,
            updated_at: payload.new.updated_at,
            user: user,
            type: 'message'
          };

          // Add parent message data if available
          if (parentMessageData) {
            message.parent_message = parentMessageData;
          }

          console.log('[Subscription] Transformed message:', message);
          callback(message);
        } else if (payload.new.record_type === 'poll') {
          // Handle poll creation
          const { data: creator } = await supabase
            .from('users')
            .select('id, name, email, is_active, inactive_user_name')
            .eq('id', payload.new.created_by)
            .single();

          const poll = {
            id: payload.new.id,
            channel_id: payload.new.channel_id,
            created_by: payload.new.created_by,
            question: payload.new.poll_question,
            options: payload.new.poll_options,
            ends_at: payload.new.poll_ends_at,
            created_at: payload.new.created_at,
            user: creator,
            votes: [],
            type: 'poll'
          };

          console.log('[Subscription] Transformed poll:', poll);
          callback(poll);
        }
      }
    )
    .subscribe((status) => {
      console.log('[Subscription] Status changed:', channelName, status);
    });

  return subscription;
};

/**
 * Subscribe to message updates in a channel
 */
export const subscribeToMessageUpdates = (channelId, callback) => {
  const subscription = supabase
    .channel(`message-updates:${channelId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'team_chat',
        filter: `channel_id=eq.${channelId}`
      },
      (payload) => {
        // Only process message records
        if (payload.new.record_type !== 'message') return;

        // Transform to match expected format
        const message = {
          id: payload.new.id,
          channel_id: payload.new.channel_id,
          user_id: payload.new.user_id,
          message: payload.new.message_text,
          mentions: payload.new.mentions,
          is_edited: payload.new.is_edited,
          created_at: payload.new.created_at,
          updated_at: payload.new.updated_at
        };

        callback(message);
      }
    )
    .subscribe();

  return subscription;
};

/**
 * Subscribe to message deletions in a channel
 */
export const subscribeToMessageDeletions = (channelId, callback) => {
  const subscription = supabase
    .channel(`message-deletions:${channelId}`)
    .on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: 'team_chat',
        filter: `channel_id=eq.${channelId}`
      },
      (payload) => {
        // Only process message records
        if (payload.old.record_type !== 'message') return;

        callback(payload.old.id);
      }
    )
    .subscribe();

  return subscription;
};

/**
 * Unsubscribe from channel
 */
export const unsubscribeFromChannel = async (subscription) => {
  if (subscription) {
    await supabase.removeChannel(subscription);
  }
};

/**
 * Subscribe to typing indicators in a channel
 * Uses Supabase Broadcast for real-time typing status
 */
export const subscribeToTyping = (channelId, onTyping) => {
  const channel = supabase.channel(`typing:${channelId}`)
    .on('broadcast', { event: 'typing' }, (payload) => {
      onTyping(payload.payload);
    })
    .subscribe();

  return channel;
};

/**
 * Broadcast typing status to channel
 */
export const broadcastTyping = async (channelId, userId, userName, isTyping) => {
  const channel = supabase.channel(`typing:${channelId}`);

  await channel.send({
    type: 'broadcast',
    event: 'typing',
    payload: {
      user_id: userId,
      user_name: userName,
      is_typing: isTyping,
      timestamp: Date.now()
    }
  });

  return channel;
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Extract mentions from message text
 * Format: @username
 */
export const extractMentions = (message, users) => {
  const mentionRegex = /@(\w+)/g;
  const matches = message.match(mentionRegex);

  if (!matches) return [];

  const mentionedUserIds = [];
  matches.forEach(match => {
    const username = match.substring(1);
    const user = users.find(u => u.name.toLowerCase() === username.toLowerCase());
    if (user) {
      mentionedUserIds.push(user.id);
    }
  });

  return mentionedUserIds;
};

/**
 * Format message with highlighted mentions
 */
export const formatMessageWithMentions = (message) => {
  return message.replace(/@(\w+)/g, '<span class="text-blue-400 font-medium">@$1</span>');
};

/**
 * Check if user is channel admin
 */
export const isChannelAdmin = (channelMembers, userId) => {
  const member = channelMembers.find(m => m.user_id === userId);
  return member?.role === 'admin';
};

/**
 * Group messages by date
 */
export const groupMessagesByDate = (messages) => {
  const grouped = {};

  messages.forEach(message => {
    const date = new Date(message.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(message);
  });

  return grouped;
};

// ============================================
// REAL-TIME SUBSCRIPTIONS - CHANNELS & MEMBERS
// ============================================

/**
 * Subscribe to channel changes (create, update, delete)
 */
export const subscribeToChannels = (callback) => {
  const subscription = supabase
    .channel('channels-changes')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'team_chat',
        filter: 'record_type=eq.channel'
      },
      async (payload) => {
        console.log('[Subscription] Channel created:', payload);
        const channel = {
          id: payload.new.id,
          name: payload.new.channel_name,
          description: payload.new.channel_description,
          is_private: payload.new.is_private,
          created_by: payload.new.created_by,
          created_at: payload.new.created_at,
          updated_at: payload.new.updated_at
        };
        callback({ event: 'INSERT', channel });
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'team_chat',
        filter: 'record_type=eq.channel'
      },
      async (payload) => {
        console.log('[Subscription] Channel updated:', payload);
        const channel = {
          id: payload.new.id,
          name: payload.new.channel_name,
          description: payload.new.channel_description,
          is_private: payload.new.is_private,
          created_by: payload.new.created_by,
          created_at: payload.new.created_at,
          updated_at: payload.new.updated_at
        };
        callback({ event: 'UPDATE', channel });
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: 'team_chat',
        filter: 'record_type=eq.channel'
      },
      (payload) => {
        console.log('[Subscription] Channel deleted:', payload);
        callback({ event: 'DELETE', channelId: payload.old.id });
      }
    )
    .subscribe((status) => {
      console.log('[Subscription] Channels subscription status:', status);
    });

  return subscription;
};

/**
 * Subscribe to member changes in a specific channel
 */
export const subscribeToChannelMembers = (channelId, callback) => {
  const subscription = supabase
    .channel(`members:${channelId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'team_chat',
        filter: `channel_id=eq.${channelId}`
      },
      async (payload) => {
        // Only process member records
        if (payload.new.record_type !== 'member') return;

        console.log('[Subscription] Member added:', payload);

        // Fetch user details
        const { data: user } = await supabase
          .from('users')
          .select('id, name, email, is_active, inactive_user_name')
          .eq('id', payload.new.user_id)
          .single();

        const member = {
          id: payload.new.id,
          channel_id: payload.new.channel_id,
          user_id: payload.new.user_id,
          role: payload.new.user_role,
          joined_at: payload.new.created_at,
          user: user
        };

        callback({ event: 'INSERT', member });
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: 'team_chat',
        filter: `channel_id=eq.${channelId}`
      },
      (payload) => {
        // Only process member records
        if (!payload.old || payload.old.record_type !== 'member') return;

        console.log('[Subscription] Member removed - ID:', payload.old.id, 'User ID:', payload.old.user_id);
        callback({ event: 'DELETE', memberId: payload.old.id, userId: payload.old.user_id });
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'team_chat',
        filter: `channel_id=eq.${channelId}`
      },
      async (payload) => {
        // Only process member records
        if (payload.new.record_type !== 'member') return;

        console.log('[Subscription] Member updated:', payload);

        // Fetch user details
        const { data: user } = await supabase
          .from('users')
          .select('id, name, email, is_active, inactive_user_name')
          .eq('id', payload.new.user_id)
          .single();

        const member = {
          id: payload.new.id,
          channel_id: payload.new.channel_id,
          user_id: payload.new.user_id,
          role: payload.new.user_role,
          joined_at: payload.new.created_at,
          user: user
        };

        callback({ event: 'UPDATE', member });
      }
    )
    .subscribe((status) => {
      console.log('[Subscription] Members subscription status:', status);
    });

  return subscription;
};
